import React, { useMemo, useState } from "react";
import { TypeItem } from "@/lib/stores/typesStore";
import { objectsStore, GlossaryItem } from "@/lib/stores/objectsStore";
import { templatesStore } from "@/lib/stores/templatesStore";
import { GlossaryBlueprint } from "@/types-games";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, ExternalLink, FileText, Library, LayoutPanelTop } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

interface UsageReference {
  itemId: string;
  itemName: string;
  itemType: "library" | "template" | "dialog";
  fieldPath: string; // e.g., "Section Name > Field Name"
  fieldId?: string;
  sectionId?: string;
}

interface VariableUsagePanelProps {
  variable: TypeItem;
  gameId: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

/**
 * Panel that shows where a variable/type is being used across library items,
 * templates, and dialogs.
 */
export const VariableUsagePanel: React.FC<VariableUsagePanelProps> = ({
  variable,
  gameId,
  isCollapsed = true,
  onToggleCollapse,
}) => {
  const navigate = useNavigate();
  const [localCollapsed, setLocalCollapsed] = useState(isCollapsed);
  
  const collapsed = onToggleCollapse ? isCollapsed : localCollapsed;
  const toggleCollapse = onToggleCollapse || (() => setLocalCollapsed(!localCollapsed));

  // Find all usages of this type/variable
  const usages = useMemo(() => {
    const refs: UsageReference[] = [];
    
    // Search in library items (objects)
    const objects = objectsStore.getData().filter(obj => obj.game_id === gameId);
    objects.forEach((obj: GlossaryItem) => {
      // Check if the object uses this type directly
      if (obj.type === variable.id) {
        refs.push({
          itemId: obj.id,
          itemName: obj.name,
          itemType: "library",
          fieldPath: "Object Type",
        });
      }
      
      // Check in section fields
      if (obj.data && Array.isArray(obj.data)) {
        obj.data.forEach((section: any) => {
          if (section.fields && Array.isArray(section.fields)) {
            section.fields.forEach((field: any) => {
              // Check if field type matches this variable
              if (field.type === variable.id || field.type === variable.name) {
                refs.push({
                  itemId: obj.id,
                  itemName: obj.name,
                  itemType: "library",
                  fieldPath: `${section.title || section.name || 'Section'} > ${field.label || field.name || 'Field'}`,
                  fieldId: field.id,
                  sectionId: section.id,
                });
              }
              // Check if field value references this type (for type selection fields)
              if (field.value === variable.id || (Array.isArray(field.value) && field.value.includes(variable.id))) {
                refs.push({
                  itemId: obj.id,
                  itemName: obj.name,
                  itemType: "library",
                  fieldPath: `${section.title || section.name || 'Section'} > ${field.label || field.name || 'Field'} (value)`,
                  fieldId: field.id,
                  sectionId: section.id,
                });
              }
            });
          }
        });
      }
    });

    // Search in templates (blueprints)
    const templates = templatesStore.getData().filter(t => t.game_id === gameId);
    templates.forEach((template: GlossaryBlueprint) => {
      // Check in template sections and fields
      if (template.data && Array.isArray(template.data)) {
        template.data.forEach((section: any) => {
          if (section.fields && Array.isArray(section.fields)) {
            section.fields.forEach((field: any) => {
              // Check if field type matches this variable
              if (field.type === variable.id || field.type === variable.name) {
                refs.push({
                  itemId: template.id,
                  itemName: template.name,
                  itemType: "template",
                  fieldPath: `${section.title || section.name || 'Section'} > ${field.label || field.name || 'Field'}`,
                  fieldId: field.id,
                  sectionId: section.id,
                });
              }
              // Check for type_id in field configuration
              if (field.type_id === variable.id) {
                refs.push({
                  itemId: template.id,
                  itemName: template.name,
                  itemType: "template",
                  fieldPath: `${section.title || section.name || 'Section'} > ${field.label || field.name || 'Field'} (type constraint)`,
                  fieldId: field.id,
                  sectionId: section.id,
                });
              }
            });
          }
        });
      }
    });

    return refs;
  }, [variable, gameId]);

  // Group usages by item type
  const groupedUsages = useMemo(() => {
    const groups: Record<string, UsageReference[]> = {
      library: [],
      template: [],
      dialog: [],
    };
    
    usages.forEach(usage => {
      groups[usage.itemType].push(usage);
    });
    
    return groups;
  }, [usages]);

  const totalCount = usages.length;

  const handleNavigate = (usage: UsageReference) => {
    switch (usage.itemType) {
      case "library":
        navigate(`/library/${usage.itemId}`);
        break;
      case "template":
        navigate(`/templates/${usage.itemId}`);
        break;
      case "dialog":
        navigate(`/dialogs/${usage.itemId}`);
        break;
    }
  };

  const getIcon = (itemType: string) => {
    switch (itemType) {
      case "library":
        return <Library className="h-4 w-4" />;
      case "template":
        return <LayoutPanelTop className="h-4 w-4" />;
      case "dialog":
        return <FileText className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="border rounded-lg mt-4">
      {/* Header */}
      <button
        onClick={toggleCollapse}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">Usage</span>
          <span className="text-xs px-2 py-0.5 bg-secondary rounded-full">
            {totalCount} {totalCount === 1 ? "reference" : "references"}
          </span>
        </div>
        {collapsed ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronUp className="h-4 w-4" />
        )}
      </button>

      {/* Content */}
      {!collapsed && (
        <div className="border-t p-3">
          {totalCount === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              This variable is not used anywhere yet.
            </p>
          ) : (
            <div className="space-y-4">
              {/* Library Items */}
              {groupedUsages.library.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Library className="h-4 w-4" />
                    Library Items ({groupedUsages.library.length})
                  </h4>
                  <div className="space-y-1">
                    {groupedUsages.library.map((usage, idx) => (
                      <UsageItem
                        key={`library-${idx}`}
                        usage={usage}
                        onNavigate={handleNavigate}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Templates */}
              {groupedUsages.template.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                    <LayoutPanelTop className="h-4 w-4" />
                    Templates ({groupedUsages.template.length})
                  </h4>
                  <div className="space-y-1">
                    {groupedUsages.template.map((usage, idx) => (
                      <UsageItem
                        key={`template-${idx}`}
                        usage={usage}
                        onNavigate={handleNavigate}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Dialogs */}
              {groupedUsages.dialog.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4" />
                    Dialogs ({groupedUsages.dialog.length})
                  </h4>
                  <div className="space-y-1">
                    {groupedUsages.dialog.map((usage, idx) => (
                      <UsageItem
                        key={`dialog-${idx}`}
                        usage={usage}
                        onNavigate={handleNavigate}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Individual usage item
interface UsageItemProps {
  usage: UsageReference;
  onNavigate: (usage: UsageReference) => void;
}

const UsageItem: React.FC<UsageItemProps> = ({ usage, onNavigate }) => {
  return (
    <div
      className={cn(
        "flex items-center justify-between p-2 rounded hover:bg-muted/50 cursor-pointer group",
        "transition-colors"
      )}
      onClick={() => onNavigate(usage)}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{usage.itemName}</p>
        <p className="text-xs text-muted-foreground truncate">{usage.fieldPath}</p>
      </div>
      <ExternalLink className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2" />
    </div>
  );
};

export default VariableUsagePanel;
